import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Professional, User, AuthResponse } from '../types';
import { api } from '../services/api';

interface AuthContextType {
  professional: Professional | null;
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  loading: boolean;
  updateProfessional: (updatedProfessional: Professional) => void;
  updateUser: (updatedUser: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [professional, setProfessional] = useState<Professional | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('token');
      
      if (storedToken) {
        try {
          api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          
          // Get user profile
          const response = await api.get('/auth/profile');
          const userData = response.data.user;
          
          setUser(userData);
          setToken(storedToken);
          
          // If user is a professional, also get professional data
          if (userData.role === 'professional' && userData.professional) {
            setProfessional(userData.professional);
          }
        } catch (error) {
          localStorage.removeItem('token');
          delete api.defaults.headers.common['Authorization'];
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      const response = await api.post('/auth/login', { email, password });
      const { token: newToken, user: newUser, professional: newProfessional } = response.data as AuthResponse;
      
      localStorage.setItem('token', newToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      setToken(newToken);
      setUser(newUser);
      
      // If the response includes professional data, use it
      if (newProfessional) {
        setProfessional(newProfessional);
      } else if (newUser.role === 'professional') {
        // Professional data should be included in the user response from PostgreSQL
        setProfessional(newUser.professional || null);
      } else {
        setProfessional(null);
      }
      
      setLoading(false);
      return newUser;
    } catch (error: any) {
      setLoading(false);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setToken(null);
    setProfessional(null);
    setUser(null);
  };

  const updateProfessional = (updatedProfessional: Professional) => {
    setProfessional(updatedProfessional);
    
    // Also update the user if the professional data includes user updates
    if (updatedProfessional.user) {
      setUser(prevUser => prevUser ? { ...prevUser, ...updatedProfessional.user } : null);
    }
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  const value = {
    professional,
    user,
    token,
    login,
    logout,
    loading,
    updateProfessional,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 